import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'components/ui/dialog/Dialog';
import EnhancedRaisedButton from 'components/button/EnhancedRaisedButton';
import Loader from 'components/loading/Loader';
import RenderPriceInputField from 'components/form/InputFields/RenderPriceInputField';
import renderTextareaInputField from 'components/form/InputFields/RenderTextareaInputField';
import SelectField from 'components/input/SelectField/SelectField';
import SubmitButton from 'components/button/SubmitButton';
import { Field, reduxForm } from 'redux-form';
import { FormWrapper, Item, Row, Section } from 'components/ui/table/elements';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showNotification } from 'store/actions/uiActions';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import validations from 'helpers/forms/validations';

const DialogLoader = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.7);
`;

const StyledButtonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
    margin-left: auto;
    column-gap: 20px;
    width: fit-content;
`;

class UserWalletTopUpModal extends Component {
    state = {
        showModal: false,
        loading: false,
    };

    toggleModal = () => {
        this.setState((state) => {
            return {
                showModal: !state.showModal,
            };
        });
    }

    toggleLoading = () => {
        this.setState((state) => {
            return {
                loading: !state.loading,
            };
        });
    }

    handleSubmitForm = (values) => {
        this.toggleLoading();
        const { refreshWallets, showNotification, translate } = this.props;

        const { id, ...params } = utils.pick(values, [
            'id',
            'amount',
            'reason',
            'type',
        ]);

        return apiClient.create(`${ config.getApiBaseUrl() }/wallets/${ id }/change`, params)
            .then(
                () => {
                    this.toggleLoading();

                    this.toggleModal();

                    refreshWallets();
                    showNotification(translate(`resources.wallets.topup.notification.success.${ params.type }`), 'success');
                },
                (error) => {
                    this.toggleLoading();
                    showNotification(error.message, 'warning');
                }
            );
    }

    render() {
        const {
            handleSubmit,
            invalid,
            pristine,
            submitting,
            translate,
        } = this.props;

        const {
            loading,
            showModal,
        } = this.state;

        return (
            <div>
                <EnhancedRaisedButton
                    label={ translate('resources.wallets.topup.label') }
                    onClick={ () => {
                        this.toggleModal();
                    } }
                    primary={ true }
                />

                <Dialog
                    actions={ null }
                    actionsContainerStyle={ {
                        padding: '20px 20px 20px 0',
                    } }
                    bodyStyle={ {
                        padding: '0 20px',
                        margin: '20px 0',
                    } }

                    modal={ showModal }
                    autoScrollBodyContent={ true }
                    autoDetectWindowHeight={ true }
                    onRequestClose={ () => {
                        this.toggleModal();
                    } }
                    open={ showModal }
                    overlayStyle={ {
                        backgroundColor: 'rgba(237, 236, 236, 0.6)',
                    } }
                    title={ translate('resources.wallets.topup.title') }
                    titleStyle={ {
                        padding: '30px 20px 10px',
                        margin: '0',
                    } }
                >
                    <FormWrapper>
                        <form onSubmit={ handleSubmit(this.handleSubmitForm) }>
                            <Section>
                                <Row>
                                    <Item>
                                        <Field
                                            choices={ [
                                                {
                                                    id: 'increase',
                                                    name: translate('actions.add'),
                                                },
                                                {
                                                    id: 'decrease',
                                                    name: translate('actions.deduct'),
                                                },
                                            ] }
                                            component={ SelectField }
                                            floatingLabelFixed={ true }
                                            label={ translate('resources.wallets.fields.type') }
                                            name='type'
                                            validate={ [
                                                validations.required,
                                            ] }
                                        />
                                    </Item>
                                </Row>
                                <Row>
                                    <Item>
                                        <Field
                                            component={ RenderPriceInputField }
                                            floatingLabelFixed={ true }
                                            label={ translate('resources.wallets.fields.amount') }
                                            name='amount'
                                            validate={ [
                                                validations.required,
                                                validations.number,
                                            ] }
                                        />
                                    </Item>
                                    <Item>
                                        <Field
                                            component={ RenderPriceInputField }
                                            floatingLabelFixed={ true }
                                            disabled={ true }
                                            label={ translate('resources.wallets.fields.balance') }
                                            name='balance'
                                        />
                                    </Item>
                                </Row>
                                <Row>
                                    <Item>
                                        <Field
                                            component={ renderTextareaInputField }
                                            floatingLabelFixed={ true }
                                            label={ translate('resources.wallets.fields.reason') }
                                            maxLength={ 255 }
                                            multiLine={ true }
                                            name='reason'
                                            rows={ 4 }
                                            validate={ validations.required }
                                        />
                                    </Item>
                                </Row>
                            </Section>
                            <Section>
                                <StyledButtonWrapper>
                                    <EnhancedRaisedButton
                                        label={ translate('actions.close') }
                                        primary={ true }
                                        onClick={ () => {
                                            return this.toggleModal();
                                        } }
                                    />
                                    <SubmitButton
                                        loading={ submitting }
                                        disabled={ pristine || submitting || invalid }
                                        primary={ true }
                                    />
                                </StyledButtonWrapper>
                            </Section>
                        </form>
                    </FormWrapper>

                    { loading &&
                        <DialogLoader>
                            <Loader size={ 50 } />
                        </DialogLoader>
                    }
                </Dialog>
            </div>
        );
    }
}

UserWalletTopUpModal.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    invalid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    refreshWallets: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => {
    return {
        showNotification: (message, type) => {
            dispatch(showNotification(message, type));
        },
    };
};

const enhance = compose(
    translate,
    connect(null, mapDispatchToProps),
    reduxForm({
        destroyOnUnmount: true,
        form: 'user-wallet-top-up',
    }),
);

export default enhance(UserWalletTopUpModal);



// WEBPACK FOOTER //
// ./src/pages/users/wallets/UserWalletTopUpModal.js